import React, { useContext, useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AppContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { CONSTANTS } from 'src/components/Constants';
import { RankCard, AiSubsectionHeader, Empty } from './clinicalInsightsUi';
import {
  CASCADE_BADGE_COLORS,
  getCascadeBadgeTier
} from '../../pedigree-tool/analysis/cascadeBadge';

// Renders the (pure, client-side) pedigree-analysis result in the Clinical
// Insights sidebar. Family view (no node selected): inheritance pattern,
// family-aware syndromes, cascade testing, review alerts. Individual view:
// the selected member's role, tags and recommended actions.

const PATTERN_LABEL = {
  'pattern.autosomal_dominant': 'Autosomal dominant',
  'pattern.autosomal_recessive': 'Autosomal recessive',
  'pattern.x_linked_dominant': 'X-linked dominant',
  'pattern.x_linked_recessive': 'X-linked recessive',
  'pattern.mitochondrial': 'Mitochondrial',
  'pattern.y_linked': 'Y-linked'
};
const ROLE_LABEL = {
  proband: 'Proband',
  affected: 'Affected',
  obligate_carrier: 'Obligate carrier',
  tested_carrier: 'Tested carrier',
  reported_carrier: 'Reported carrier',
  carrier: 'Carrier',
  cascade_candidate: 'Cascade candidate',
  unaffected_at_risk_relative: 'Unaffected at-risk relative',
  unaffected: 'Unaffected',
  unknown: 'Unknown'
};
const ACTION_LABEL = {
  targeted_test: 'Targeted test',
  panel_test: 'Panel test',
  counselling_first: 'Counselling first',
  defer: 'Defer'
};
// The role is the single highest-precedence classification; tags list every
// attribute that applies. Each role (bar unaffected/unknown) maps to one tag —
// we drop that tag so the role isn't rendered twice as a chip.
const ROLE_TO_TAG = {
  proband: 'proband',
  affected: 'affected',
  obligate_carrier: 'obligate carrier',
  tested_carrier: 'tested carrier',
  reported_carrier: 'reported carrier',
  carrier: 'carrier',
  cascade_candidate: 'cascade candidate'
};
const CONF_COLOR = {
  strong: '#2e7d32',
  moderate: '#ed6c02',
  weak: '#9e9e9e',
  ruled_out: '#c62828'
};

const ConfidenceChip = ({ confidence }) =>
  confidence ? (
    <Chip
      label={confidence.replace('_', ' ')}
      size="small"
      sx={{
        height: 18,
        fontSize: '.6rem',
        textTransform: 'capitalize',
        bgcolor: CONF_COLOR[confidence] || '#9e9e9e',
        color: '#fff'
      }}
    />
  ) : null;

const UpnChip = ({ upn }) => (
  <Chip
    label={`UPN ${upn}`}
    size="small"
    variant="outlined"
    sx={{ height: 18, fontSize: '.6rem' }}
  />
);

// "Father's Father" → "Father's father" (leading capital, rest lowercase).
const toSentenceCase = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

// Last-resort prettifier for an analyzer token (e.g. "IND_3") when the live
// member record can't be found. Real relationships resolve via CONSTANTS.
const humanizeToken = (token) => {
  if (!token) return 'Relative';
  if (token === 'PROBAND') return 'Proband';
  return toSentenceCase(token.replace(/_/g, ' '));
};

// Readable relationship for a cascade tile. Prefer the live member's
// `relationship` key (mapped via CONSTANTS.relations); fall back to the token.
const relationshipLabel = (member, token) => {
  if (member?.proBandId) return 'Proband';
  const label =
    member?.relationship && CONSTANTS.relations[member.relationship];
  return label ? toSentenceCase(label) : humanizeToken(token);
};

// Proband is conventionally UPN 1 (mirrors formatPersonSummary in the sidebar).
const upnOf = (member) =>
  member ? (member.proBandId ? 1 : member.upn ?? null) : null;

export default function PedigreeMatchMenu({ analysis, data, memberRecords }) {
  const t = useAutoTranslation();
  const { content } = useContext(AppContext);

  const diseaseName = useMemo(() => {
    const map = {};
    for (const d of content?.pedigreeSelectedUsers?.diagnoses || [])
      map[d.id] = d.name;
    return (id) => map[id] || id;
  }, [content]);

  // pid → live member record, for resolving UPN + relationship on tiles.
  const memberByPid = useMemo(() => {
    const map = {};
    for (const m of memberRecords || []) map[String(m.pid)] = m;
    return map;
  }, [memberRecords]);

  if (!analysis) return <Empty text={t('No pedigree analysis available.')} />;

  // ---- Individual view ----
  if (data) {
    const section = analysis.individuals?.[String(data.pid)];
    if (!section) return <Empty text={t('No analysis for this individual.')} />;
    const patternLabels = (section.referencedPatterns || [])
      .map((id) => t(PATTERN_LABEL[id] || id))
      .join(', ');
    // Drop the tag the role already represents (e.g. role "Affected" + tag
    // "affected"); keep only the additional attributes.
    const extraTags = (section.tags || []).filter(
      (tag) => tag !== ROLE_TO_TAG[section.role]
    );
    return (
      <Box sx={{ p: 0.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={t(ROLE_LABEL[section.role] || section.role)}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '.65rem' }}
          />
          {extraTags.map((tag) => (
            <Chip
              key={tag}
              label={t(tag)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '.65rem' }}
            />
          ))}
        </Box>
        {patternLabels && (
          <Typography
            sx={{ fontSize: '.75rem', color: 'text.secondary', mb: 1 }}
          >
            {t('Contributes to family pattern')}: {patternLabels}
          </Typography>
        )}
        {section.individualActions?.length > 0 && (
          <>
            <AiSubsectionHeader>{t('Recommended actions')}</AiSubsectionHeader>
            {section.individualActions.map((a, i) => (
              <RankCard
                key={i}
                accent="#ed6c02"
                title={t(ACTION_LABEL[a.recommendedAction] || 'Action')}
                meta={`${Math.round((a.priorProbability || 0) * 100)}% prior${
                  a.variant?.gene ? ` · ${a.variant.gene}` : ''
                }${a.deferReason ? ` · deferred (${a.deferReason})` : ''}`}
              />
            ))}
          </>
        )}
      </Box>
    );
  }

  // ---- Family view ----
  const fam = analysis.family || {};
  const patterns = fam.inheritancePatterns || [];
  const syndromes = fam.syndromes || [];
  const cascade = fam.cascadeCandidates || [];
  const alerts = fam.alerts || [];
  const hasAny =
    patterns.length || syndromes.length || cascade.length || alerts.length;

  // Group cascade candidates by family member: one tile per person listing
  // every disease they're a candidate for. Tiles ordered by the member's
  // highest prior (so first-degree / highest-yield people surface first);
  // diseases within a tile ordered by prior too.
  const cascadeGroups = (() => {
    const groups = new Map();
    for (const c of cascade) {
      if (!groups.has(c.pid))
        groups.set(c.pid, { pid: c.pid, token: c.token, items: [] });
      groups.get(c.pid).items.push(c);
    }
    return [...groups.values()]
      .map((g) => {
        const items = [...g.items].sort(
          (a, b) => (b.priorProbability || 0) - (a.priorProbability || 0)
        );
        return {
          ...g,
          items,
          topPrior: items[0]?.priorProbability || 0,
          allDefer: items.every((i) => i.recommendedAction === 'defer')
        };
      })
      .sort((a, b) => b.topPrior - a.topPrior);
  })();

  if (!hasAny)
    return (
      <Empty
        text={t(
          'No pedigree-level findings yet. Add affected relatives, diagnoses and carrier status to generate insights.'
        )}
      />
    );

  return (
    <Box sx={{ p: 0.5 }}>
      {patterns.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Inheritance pattern')}</AiSubsectionHeader>
          {patterns.map((p, i) => (
            <RankCard
              key={i}
              accent="#2ba9bf"
              title={t(PATTERN_LABEL[p.id] || p.id)}
              meta={diseaseName(p.diseaseId)}
              supportingMemberIds={p.supportingIds}
              right={<ConfidenceChip confidence={p.confidence} />}
              lines={p.rationale?.slice(0, 1).map((r) => t(r))}
            />
          ))}
        </>
      )}

      {syndromes.length > 0 && (
        <>
          <AiSubsectionHeader>
            {t('Syndromes (family-aware)')}
          </AiSubsectionHeader>
          {syndromes.map((s, i) => (
            <RankCard
              key={i}
              accent="#7b1fa2"
              title={t(s.label)}
              meta={s.omim ? `OMIM:${s.omim}` : null}
              supportingMemberIds={s.supportingIds}
              right={<ConfidenceChip confidence={s.confidence} />}
              lines={s.rationale?.slice(0, 1).map((r) => t(r))}
            />
          ))}
        </>
      )}

      {cascadeGroups.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Cascade testing')}</AiSubsectionHeader>
          {cascadeGroups.map((g) => {
            const member = memberByPid[String(g.pid)];
            const upn = upnOf(member);
            return (
              <RankCard
                key={g.pid}
                accent={
                  CASCADE_BADGE_COLORS[
                    getCascadeBadgeTier({
                      priorProbability: g.topPrior,
                      recommendedAction: g.allDefer ? 'defer' : null
                    })
                  ]
                }
                title={relationshipLabel(member, g.token)}
                supportingMemberIds={[g.pid]}
                right={upn != null ? <UpnChip upn={upn} /> : null}
                lines={g.items.map((c) => {
                  const dz = c.diseaseId
                    ? diseaseName(c.diseaseId)
                    : t('Disease');
                  const gene = c.variant?.gene ? ` · ${c.variant.gene}` : '';
                  const defer = c.deferReason ? ` (${t(c.deferReason)})` : '';
                  // Obligate-by-pedigree (~100%): route to the confirm-obligate
                  // workflow, not screening — don't render a prior/test action.
                  if (c.obligate)
                    return `${dz} · ${t('Obligate — confirm')}${gene}${defer}`;
                  const pct = Math.round((c.priorProbability || 0) * 100);
                  const action = t(
                    ACTION_LABEL[c.recommendedAction] || c.recommendedAction
                  );
                  return `${dz} · ${pct}% prior · ${action}${gene}${defer}`;
                })}
              />
            );
          })}
        </>
      )}

      {alerts.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Review needed')}</AiSubsectionHeader>
          {alerts.map((a, i) => (
            <RankCard
              key={i}
              accent={a.severity === 'review' ? '#c62828' : '#ed6c02'}
              title={t(a.detail)}
              right={
                <Chip
                  label={a.severity}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '.6rem',
                    textTransform: 'capitalize'
                  }}
                />
              }
            />
          ))}
        </>
      )}
    </Box>
  );
}
